import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuthStore } from '../hooks/useAuth'

// Time-range filter options (days; 0 = all time)
const RANGES: { key: string; label: string; days: number }[] = [
  { key: '1d',  label: '24h',      days: 1 },
  { key: '7d',  label: '7 days',   days: 7 },
  { key: '30d', label: '30 days',  days: 30 },
  { key: '90d', label: '3 months', days: 90 },
  { key: 'all', label: 'All',      days: 0 },
]

// Topic filter options (match the classifier's topic enum)
const TOPICS: [string, string][] = [
  ['all', 'All topics'], ['company', 'Company'], ['tariff', 'Tariff'], ['trade_deal', 'Trade deal'],
  ['china', 'China'], ['war', 'War'], ['fed', 'Fed'], ['energy', 'Energy'], ['oil', 'Oil'],
  ['crypto', 'Crypto'], ['defense', 'Defense'], ['gold', 'Gold'], ['housing', 'Housing'],
  ['shutdown', 'Shutdown'], ['currency', 'Currency'], ['court', 'Court'], ['tech', 'Tech'],
  ['market', 'Market'], ['other', 'Other'],
]

// ─── Shared badge row — renders a signal's market-impact tags ────────────────
function SignalBadges({ s }: { s: any }) {
  const dir = s.direction ?? 'HOLD'
  const strength = s.strength ?? 0
  const isStock = s.instrumentType === 'stock'
  return (
    <div className="djt-signals" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <div style={{
        padding: '4px 10px', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.7rem',
        background: dir === 'BUY' ? 'rgba(133,196,124,.12)' : dir === 'SELL' ? 'rgba(224,92,92,.12)' : 'rgba(128,128,128,.08)',
        color: dir === 'BUY' ? 'var(--cash)' : dir === 'SELL' ? 'var(--red)' : 'var(--muted)',
        border: `1px solid ${dir === 'BUY' ? 'var(--cash-dim)' : dir === 'SELL' ? 'rgba(224,92,92,.3)' : 'var(--edge)'}`,
      }}>
        {dir === 'BUY' ? '▲' : dir === 'SELL' ? '▼' : '—'} {dir}
      </div>
      {/* Instrument — stocks highlighted + tagged */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 2,
        fontFamily: 'Syne Mono', fontSize: '.65rem', fontWeight: isStock ? 700 : 400,
        color: isStock ? 'var(--cash)' : 'var(--bill)',
        border: `1px solid ${isStock ? 'var(--cash-dim)' : 'var(--edge)'}`,
        background: isStock ? 'rgba(133,196,124,.08)' : 'transparent',
      }}>
        {s.instrument}
        <span style={{ fontSize: '.45rem', letterSpacing: '.08em', opacity: .7 }}>{isStock ? 'STOCK' : 'ETF'}</span>
      </div>
      <div style={{ padding: '4px 10px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--amber)', border: '1px solid rgba(212,168,67,.2)' }}>{s.topic}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 2, border: '1px solid var(--edge)' }}>
        <span style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)' }}>STR</span>
        <div style={{ width: 50, height: 4, background: 'var(--edge)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${strength * 10}%`, height: '100%', background: strength >= 7 ? 'var(--red)' : strength >= 4 ? 'var(--amber)' : 'var(--muted)', borderRadius: 2 }} />
        </div>
        <span style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--text)' }}>{strength}/10</span>
      </div>
      <div style={{ padding: '4px 10px', borderRadius: 2, fontFamily: 'Syne Mono', fontSize: '.6rem', color: s.urgency === 'high' ? 'var(--red)' : 'var(--muted)', border: `1px solid ${s.urgency === 'high' ? 'rgba(224,92,92,.3)' : 'var(--edge)'}` }}>{s.urgency}</div>
    </div>
  )
}

export default function DJT() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const isSuper  = user?.tier === 'super'

  const [rangeKey, setRangeKey] = useState('90d')
  const [topic, setTopic] = useState('all')
  const [search, setSearch] = useState('')
  const [showAudit, setShowAudit] = useState(false)
  const range = RANGES.find(r => r.key === rangeKey) ?? RANGES[3]
  const since = range.days > 0 ? Math.floor(Date.now() / 1000) - range.days * 86400 : undefined

  const { data: latest } = useQuery({
    queryKey: ['djt-signal'], queryFn: api.djt.signal,
    enabled: isSuper, refetchInterval: 60_000, staleTime: 30_000,
  })

  const { data: feedData, isLoading } = useQuery({
    queryKey: ['djt-feed', rangeKey],
    queryFn:  () => api.djt.feed(500, since),
    enabled:  isSuper, refetchInterval: 60_000, staleTime: 30_000,
  })

  const { data: tickerData } = useQuery({
    queryKey: ['djt-tickers', rangeKey],
    queryFn:  () => api.djt.tickers(since),
    enabled:  isSuper, refetchInterval: 120_000, staleTime: 60_000,
  })

  const allFeed: any[] = feedData?.signals ?? []
  const q = search.trim().toLowerCase()
  const feed = allFeed
    .filter(r => topic === 'all' || r.topic === topic)
    .filter(r => !q || [r.instrument, r.tickers, r.excerpt, r.reason, r.direction].some((v: any) => String(v ?? '').toLowerCase().includes(q)))
  const tickers: any[] = tickerData?.tickers ?? []

  // ─── Free / Pro gate ───────────────────────────────────────────────────────
  if (!isSuper) {
    return (
      <div style={{ maxWidth: 560, margin: '60px auto', textAlign: 'center', padding: '0 20px' }}>
        <div style={{ fontSize: '2rem', marginBottom: 12 }}>🏛️</div>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.1rem', color: 'var(--bill)', marginBottom: 10 }}>DJT Intelligence</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)', lineHeight: 1.8, marginBottom: 20 }}>
          Every market-moving Truth Social post, classified in real time — direction, instrument (ETF or single stock), strength, and the reasoning behind each call.<br /><br />
          Requires Super tier.
        </div>
        <button onClick={() => navigate('/pricing')} style={{ padding: '10px 28px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.8rem', cursor: 'pointer' }}>
          Upgrade to Super — $99.99/mo →
        </button>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <span style={{ fontSize: '1.5rem' }}>🏛️</span>
        <div>
          <div className="eyebrow">Truth Social monitor · AI classification · single-stock + macro</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.3rem', color: 'var(--bill)' }}>DJT Intelligence</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: latest?.signal ? 'var(--cash)' : 'var(--edge)' }} />
          <span style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--muted)' }}>{latest?.signal ? 'LIVE' : 'IDLE'}</span>
        </div>
      </div>

      {/* Latest signal hero */}
      {latest?.signal && (
        <div style={{ marginTop: 16, background: 'var(--card)', border: '1px solid var(--cash-dim)', borderRadius: 4, padding: '16px 18px' }}>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.5rem', color: 'var(--cash)', letterSpacing: '.12em', marginBottom: 8 }}>LATEST SIGNAL</div>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.64rem', color: 'var(--text)', marginBottom: 10, lineHeight: 1.7 }}>
            "{latest.post?.excerpt?.slice(0, 280)}{(latest.post?.excerpt?.length ?? 0) > 280 ? '…' : ''}"
          </div>
          <SignalBadges s={latest.signal} />
          {latest.signal.reason && (
            <div style={{ marginTop: 10, fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)', lineHeight: 1.6 }}>💡 {latest.signal.reason}</div>
          )}
          <div style={{ marginTop: 8, fontFamily: 'Syne Mono', fontSize: '.48rem', color: 'var(--muted)', display: 'flex', gap: 12 }}>
            <span>{latest.analyzedAt ? new Date(latest.analyzedAt).toLocaleString() : ''}</span>
            {latest.post?.url && <a href={latest.post.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cash)', textDecoration: 'none' }}>View post ↗</a>}
          </div>
        </div>
      )}

      {/* Ticker Audit — every stock Trump has referenced */}
      {tickers.length > 0 && (
        <div style={{ marginTop: 20, background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4 }}>
          <div onClick={() => setShowAudit(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', cursor: 'pointer' }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.85rem', color: 'var(--bill)' }}>Ticker Audit</div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)' }}>{tickers.length} stock{tickers.length !== 1 ? 's' : ''} referenced</div>
            <span style={{ marginLeft: 'auto', fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--cash)' }}>{showAudit ? '▲' : '▼'}</span>
          </div>
          {showAudit && (
            <div style={{ padding: '0 16px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {tickers.map((t: any) => (
                <div key={t.ticker} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 3, fontFamily: 'Syne Mono', fontSize: '.58rem' }}>
                  <span style={{ fontWeight: 700, color: 'var(--cash)' }}>{t.ticker}</span>
                  <span style={{ color: 'var(--muted)' }}>{t.mentions}×</span>
                  {t.buys > 0 && <span style={{ color: 'var(--cash)' }}>▲{t.buys}</span>}
                  {t.sells > 0 && <span style={{ color: 'var(--red)' }}>▼{t.sells}</span>}
                  <span style={{ color: 'var(--muted)', opacity: .6, fontSize: '.5rem' }}>{new Date(t.lastAt * 1000).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Feed header + filters */}
      <div style={{ marginTop: 24, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.95rem', color: 'var(--bill)' }}>Market-Moving Posts</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)' }}>{feed.length} shown</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search box — filter by ticker, text, or direction */}
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search ticker or text…"
            style={{
              background: 'var(--paper)', color: 'var(--text)', border: '1px solid var(--edge)', borderRadius: 2,
              fontFamily: 'Syne Mono', fontSize: '.55rem', padding: '4px 8px', outline: 'none', width: 150,
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} title="Clear search" style={{
              background: 'transparent', color: 'var(--muted)', border: '1px solid var(--edge)', borderRadius: 2,
              fontFamily: 'Syne Mono', fontSize: '.55rem', padding: '4px 8px', cursor: 'pointer',
            }}>✕</button>
          )}
          {/* Topic dropdown */}
          <select value={topic} onChange={e => setTopic(e.target.value)} style={{
            background: 'var(--paper)', color: 'var(--text)', border: '1px solid var(--edge)', borderRadius: 2,
            fontFamily: 'Syne Mono', fontSize: '.55rem', padding: '4px 8px', cursor: 'pointer', outline: 'none',
          }}>
            {TOPICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {/* Time range */}
          <div style={{ display: 'flex', gap: 4 }}>
            {RANGES.map(r => (
              <button key={r.key} onClick={() => setRangeKey(r.key)} style={{
                padding: '4px 10px', borderRadius: 2, cursor: 'pointer', fontFamily: 'Syne Mono', fontSize: '.55rem',
                background: rangeKey === r.key ? 'var(--cash)' : 'transparent',
                color:      rangeKey === r.key ? 'var(--ink)' : 'var(--muted)',
                border: `1px solid ${rangeKey === r.key ? 'var(--cash)' : 'var(--edge)'}`,
              }}>{r.label}</button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', padding: '24px 0', textAlign: 'center' }}>Loading feed…</div>
      ) : feed.length === 0 ? (
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', padding: '32px 0', textAlign: 'center', border: '1px dashed var(--edge)', borderRadius: 4 }}>
          {q ? `No posts match "${search}" in this range.` : topic === 'all' ? 'Monitoring Truth Social for market-moving posts — none in range.' : `No "${TOPICS.find(t => t[0] === topic)?.[1]}" posts in range.`}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {feed.map((row) => (
            <div key={row.id} style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '14px 16px' }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--text)', marginBottom: 10, lineHeight: 1.7 }}>
                "{row.excerpt?.slice(0, 280)}{(row.excerpt?.length ?? 0) > 280 ? '…' : ''}"
              </div>
              <SignalBadges s={row} />
              {row.reason && (
                <div style={{ marginTop: 10, fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)', lineHeight: 1.6 }}>💡 {row.reason}</div>
              )}
              <div style={{ marginTop: 8, fontFamily: 'Syne Mono', fontSize: '.48rem', color: 'var(--muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>{row.createdAt ? new Date(row.createdAt * 1000).toLocaleString() : ''}</span>
                {row.tradesPlaced > 0 && <span style={{ color: 'var(--cash)' }}>🤖 {row.tradesPlaced} trade(s) placed</span>}
                {row.postUrl && <a href={row.postUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cash)', textDecoration: 'none' }}>View post ↗</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
